/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../api/axiosInstance"; // token JWT incluido automáticamente
import CustomerForm from "../Components/CustomerForm";
import PmsConfirmDialog from "../Components/PmsConfirmDialog";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import { formatReservationBaseAmount } from "../currency/reservationCurrency";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
} from "../currency/currencyPrecision";
import { submitWaivedTerminalWorkflow } from "../reservations/terminalReservationWorkflow";
import "../ReservationDetails.css";

function amountToCents(value) {
  const n = Number(String(value ?? "").replace(",", "."));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function centsToAmount(cents, currency, currencyCatalog = []) {
  const n = Number(cents);
  const minorUnits = getCurrencyMinorUnits(currency, currencyCatalog);
  if (!Number.isFinite(n)) return (0).toFixed(minorUnits);
  return (n / 100).toFixed(minorUnits);
}

export function getManualPaymentPrecisionError(value, currency, currencyCatalog = []) {
  if (hasValidCurrencyPrecision(value, currency, currencyCatalog)) return null;
  const code = String(currency || "").trim().toUpperCase();
  const minorUnits = getCurrencyMinorUnits(code, currencyCatalog);
  return `${code} accepts ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`;
}

function moneyShapeToCents(value) {
  if (value && typeof value === "object" && Number.isFinite(Number(value.cents))) {
    return Number(value.cents);
  }
  if (value && typeof value === "object" && value.amount != null) {
    return amountToCents(value.amount) || 0;
  }
  return amountToCents(value) || 0;
}

function normalizeManualPaymentMethod(value) {
  const key = String(value || "").trim().toLowerCase();
  if (key === "credit card" || key === "direct card" || key === "manual_card") return "manual_card";
  if (key === "bank transfer" || key === "bank_transfer") return "bank_transfer";
  return "cash";
}

function findReservationBalance(paymentBalance, reservationId) {
  const rows = Array.isArray(paymentBalance?.reservations) ? paymentBalance.reservations : [];
  return rows.find((row) => Number(row.reservation_id) === Number(reservationId)) || null;
}

async function fetchPaymentBalanceForReservation(reservationData) {
  const groupId = reservationData?.reservation_group_id;
  if (!groupId) return null;

  const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/reservation-groups/${groupId}/payment-balance`);
  return res.data || null;
}

function ReservationDetails() {
    const { displayCurrency, convertFromBase, currencyCatalog = [] } = useCurrencyDisplay();
    const currentUser = JSON.parse(localStorage.getItem("palantir_user"));
    const permissions = currentUser?.permissions || {};

    const { id } = useParams();
    const [reservation, setReservation] = useState(null);
    const [paymentBalance, setPaymentBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showPaymentForm, setShowPaymentForm] = useState(false); // Estado para mostrar formulario
    const [showRefundForm, setShowRefundForm] = useState(false);
    const [newPayment, setNewPayment] = useState({ amount_paid: "", payment_method: "Cash" });
    const [showCustomerForm, setShowCustomerForm] = useState(false);
    const [customerFormData, setCustomerFormData] = useState(null);
    const [refundData, setRefundData] = useState({
      refund_payment_id: "",
      refund_amount: "",
      refund_method: ""
    });
    
    const [maxAgeChild, setMaxAgeChild] = useState(null);
    const [maxAgeBaby, setMaxAgeBaby] = useState(null);
    const [showNoShowDialog, setShowNoShowDialog] = useState(false);
    const [isNoShowSaving, setIsNoShowSaving] = useState(false);



     useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/api/reservations/${id}`)
            .then(response => {
                setReservation(response.data);
                fetchPaymentBalanceForReservation(response.data)
                  .then((balance) => setPaymentBalance(balance))
                  .catch((error) => {
                    console.warn("Payment balance unavailable:", error?.response?.data || error?.message || error);
                    setPaymentBalance(null);
                  });
                console.log("Datos de la reserva:", response.data); // 🔹 Agregar esto
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching reservation:", error);
                setLoading(false);
            });
    }, [id]);

    useEffect(() => {
      if (!reservation?.business_unit_id) return;
    
      const fetchAgeSettings = async () => {
        try {
          const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
            params: { business_unit_id: reservation.business_unit_id }
          });
          console.log("🎯 Configuraciones cargadas:", res.data);
          setMaxAgeChild(res.data.max_age_child);
          setMaxAgeBaby(res.data.max_age_baby);
        } catch (err) {
          console.error("❌ Error al cargar edades desde hotel-settings:", err);
        }
      };
    
      fetchAgeSettings();
    }, [reservation?.business_unit_id]);
    

    // Esta función maneja los cambios en el formulario del cliente
    const handleCustomerChange = (e) => {
      const { name, value } = e.target;
      setCustomerFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    };
    

    const handleCustomerSelectChange = (selectedOption) => {
      const value = selectedOption ? selectedOption.value : "";
      setCustomerFormData((prev) => ({ ...prev, country: value }));
    };
    

    // Esta función envía la información actualizada del cliente al backend
    const handleSubmitCustomer = async (e) => {
      e.preventDefault();
    
      // 🧠 Calcular edad desde la fecha de nacimiento
      const calculateAge = (dob) => {
        const birth = new Date(dob);
        const today = new Date();
        let age = today.getFullYear() - birth.getFullYear();
        const m = today.getMonth() - birth.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
          age--;
        }
        return age;
      };
    
      const age = calculateAge(customerFormData.date_of_birth);
      console.log("🎂 Edad calculada:", age);
    
      if (reservation?.guests?.[0]?.customer?.id === customerFormData.id) {
        // Si está editando al huésped principal
        if (age <= maxAgeChild) {
          alert("Main Guest must be older than a child. Please check the birth date.");
          return;
        }
      }
    
      try {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/customers/${customerFormData.id}`,
          customerFormData
        );
        alert("Customer information updated successfully.");
        setShowCustomerForm(false);
        fetchReservation(); // Refrescar
      } catch (error) {
        console.error("Error updating customer:", error);
        alert("Failed to update customer.");
      }
    };
    
    

    useEffect(() => {
        document.body.classList.add("check-in-scroll");
    
        return () => {
            document.body.classList.remove("check-in-scroll");
        };
    }, []);

    useEffect(() => {
      window.handleExternalAgeValidation = (index, birthDate) => {
        console.log("📞 Validación desde ReservationDetails:", { birthDate });
    
        if (!birthDate || !maxAgeChild || !maxAgeBaby) {
          console.warn("⚠️ No hay suficientes datos para validar la edad.");
          return;
        }
    
        const calculateAge = (birthDate) => {
          const birth = new Date(birthDate);
          const today = new Date();
    
          let age = today.getFullYear() - birth.getFullYear();
          const hasBirthdayPassed =
            today.getMonth() > birth.getMonth() ||
            (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
    
          if (!hasBirthdayPassed) {
            age -= 1;
          }
    
          return age;
        };
    
        const age = calculateAge(birthDate);
        console.log("🔍 Edad calculada:", age);
    
        if (age <= maxAgeChild) {
          alert(`❌ This customer is too young to be a Main Guest.\nCurrent age: ${age}`);
          setCustomerFormData((prev) => ({
            ...prev,
            birth_date: ""
          }));
        } else {
          console.log("✅ Edad válida para main guest");
        }
      };
    
      return () => {
        delete window.handleExternalAgeValidation;
      };
    }, [maxAgeChild, maxAgeBaby]);
    
    

    const fetchReservation = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/reservations/${id}`);
            setReservation(response.data);
            try {
                const balance = await fetchPaymentBalanceForReservation(response.data);
                setPaymentBalance(balance);
            } catch (balanceError) {
                console.warn("Payment balance unavailable:", balanceError?.response?.data || balanceError?.message || balanceError);
                setPaymentBalance(null);
            }
        } catch (error) {
            console.error("Error fetching reservation:", error);
        }
    };

    const navigate = useNavigate();

    const handleCheckInClick = () => {
        navigate(`/check-in/${id}`);
    };

    const handleCancelReservation = async (reservationId) => {
        if (!window.confirm("Are you sure you want to cancel this reservation without applying a cancellation charge?")) return;
        const cancellationReasonInput = window.prompt("Reason for cancellation:");
        if (cancellationReasonInput === null) return;
        const cancellationReason = String(cancellationReasonInput).trim();
        if (!cancellationReason) {
            alert("A cancellation reason is required.");
            return;
        }
        
        try {
            await submitWaivedTerminalWorkflow({
                client: axios,
                apiBaseUrl: process.env.REACT_APP_API_URL,
                reservationId,
                workflow: "cancellation",
                cancellationReason,
            });
            alert("Reservation cancelled successfully.");
            fetchReservation(); // Recargar datos
        } catch (error) {
            console.error("Error cancelling reservation:", error);
            alert(error?.response?.data?.error || "Failed to cancel the reservation.");
        }
    };
    
    const handleMarkNoShow = async (reservationId) => {
        try {
            setIsNoShowSaving(true);
            await submitWaivedTerminalWorkflow({
                client: axios,
                apiBaseUrl: process.env.REACT_APP_API_URL,
                reservationId,
                workflow: "no_show",
            });
            alert("Reservation marked as No Show.");
            fetchReservation(); // Recargar datos
        } catch (error) {
            console.error("Error marking reservation as No Show:", error);
            alert(error?.response?.data?.error || "Failed to mark as No Show.");
        } finally {
            setIsNoShowSaving(false);
            setShowNoShowDialog(false);
        }
    };
    
    
    const handleAddPayment = () => {
        setShowPaymentForm(!showPaymentForm);
    };
    
    const handlePaymentChange = (e) => {
        setNewPayment({ ...newPayment, [e.target.name]: e.target.value });
    };
    
    const handleRefundChange = (e) => {
      const { name, value } = e.target;
      setRefundData(prev => ({ ...prev, [name]: value }));
    };
  

    const handleSubmitPayment = async (e) => {
      e.preventDefault();
    
      const amount = parseFloat(newPayment.amount_paid);
      if (isNaN(amount) || amount <= 0) {
        alert("Amount must be a positive number.");
        return;
      }
      const precisionError = getManualPaymentPrecisionError(
        newPayment.amount_paid,
        displayCurrency,
        currencyCatalog
      );
      if (precisionError) {
        alert(precisionError);
        return;
      }
    
      try {
        const groupId = reservation?.reservation_group_id;
        if (!groupId) {
          alert("Reservation group not found for this reservation.");
          return;
        }

        const amountCents = amountToCents(amount);
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/stripe-transactions/groups/${groupId}/manual-payment`, {
          amount: centsToAmount(amountCents, displayCurrency, currencyCatalog),
          currency: displayCurrency,
          method: normalizeManualPaymentMethod(newPayment.payment_method),
          business_unit_id: reservation.business_unit_id,
          allocation_mode: "manual",
          allocations: [
            {
              reservation_id: parseInt(id),
              amount_cents: amountCents,
            },
          ],
          note: `Manual payment from reservation #${id}`,
        });
    
        if (response.status === 201) {
          fetchReservation(); // Refrescar
          setShowPaymentForm(false);
          setNewPayment({ amount_paid: "", payment_method: "Cash" });
        }
      } catch (error) {
        console.error("Error adding payment:", error);
        alert("Failed to add payment.");
      }
    };
    
    
    const handleSubmitRefund = async (e) => {
      e.preventDefault();
    
      const refundAmount = parseFloat(refundData.refund_amount);
      if (isNaN(refundAmount) || refundAmount <= 0) {
        alert("Refund amount must be a positive number.");
        return;
      }
      const precisionError = getManualPaymentPrecisionError(
        refundData.refund_amount,
        displayCurrency,
        currencyCatalog
      );
      if (precisionError) {
        alert(precisionError);
        return;
      }
    
      if (!refundData.refund_method) {
        alert("Please select a refund method.");
        return;
      }
    
      try {
        const groupId = reservation?.reservation_group_id;
        if (!groupId) {
          alert("Reservation group not found for this reservation.");
          return;
        }

        const amountCents = amountToCents(refundAmount);
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/stripe-transactions/groups/${groupId}/manual-refund`, {
          amount: centsToAmount(amountCents, displayCurrency, currencyCatalog),
          currency: displayCurrency,
          method: normalizeManualPaymentMethod(refundData.refund_method),
          business_unit_id: reservation.business_unit_id,
          allocation_mode: "manual",
          allocations: [
            {
              reservation_id: parseInt(id),
              amount_cents: amountCents,
            },
          ],
          note: `Manual refund from reservation #${id}`,
        });
    
        if (response.status === 201) {
          fetchReservation();
          setShowRefundForm(false);
          setRefundData({ refund_payment_id: "", refund_amount: "", refund_method: "" });
        }
      } catch (error) {
        console.error("Error submitting refund:", error);
        alert("Failed to submit refund.");
      }
    };
    
    
    

    if (loading) {
        return <div>Cargando...</div>;
    }

    if (!reservation) {
        return <div>No se encontró la reserva.</div>;
    }

    // Verificar que la reserva está definida antes de calcular fechas
    const today = new Date().toISOString().split("T")[0]; // Fecha actual en formato YYYY-MM-DD
    const checkInDate = reservation.check_in_date ? reservation.check_in_date.split("T")[0] : null; // Fecha de check-in en YYYY-MM-DD

    const isCheckInTodayOrPast = checkInDate && new Date(checkInDate) <= new Date(today);
    const reservationPaymentBalance = findReservationBalance(paymentBalance, reservation.id);
    const reservationTotalCents = reservationPaymentBalance
      ? moneyShapeToCents(reservationPaymentBalance.total)
      : moneyShapeToCents(reservation.total_price);
    const reservationPaidCents = reservationPaymentBalance
      ? moneyShapeToCents(reservationPaymentBalance.allocated_paid)
      : 0;
    const reservationPendingCents = reservationPaymentBalance
      ? moneyShapeToCents(reservationPaymentBalance.balance_due)
      : Math.max(reservationTotalCents - reservationPaidCents, 0);
    const reservationPaymentStatus =
      reservationPaymentBalance?.payment_status || reservation.payment_status || "Pending";
    const formatReservationAmount = (baseAmount) => formatReservationBaseAmount({
      baseAmount,
      snapshot: reservationPaymentBalance?.currency_snapshot,
      targetCurrency: displayCurrency,
      convertFromBase,
    });

    return (

      <div>
        {reservation.guests?.length > 0 && (
        <div
          className="bg-dark text-light shadow px-4 py-3"
          style={{
            position: "fixed",
            top: "60px", // debajo de la navbar
            left: 0,
            width: "98.7%",
            zIndex: 1050,
            borderBottom: "2px solid #333"
          }}
        >
          {reservation.guests
  .filter((g) => g.is_main_guest)
  .map((guest, index) => {
    return (
      <div
        key={index}
        className="d-flex justify-content-between align-items-start w-100"
      >
        {/* Nombre grande */}
        <div style={{ fontSize: "1.4rem", fontWeight: "bold", color: "#fff" }}>
          #{guest.customer.id} &nbsp;
          {guest.customer.first_name} {guest.customer.middle_name || ""} {guest.customer.last_name} {guest.customer.second_last_name || ""}
        </div>

        {/* Datos + Botón en columna */}
        <div className="d-flex align-items-start ms-auto" style={{ gap: "12px" }}>
          {/* Datos en columna */}
          <div className="d-flex flex-column text-end" style={{ fontSize: "0.75rem", color: "#ccc" }}>
            <div><strong>Email:</strong> {guest.customer.email}</div>
            <div><strong>Phone:</strong> {guest.customer.phone}</div>
            <div><strong>Country:</strong> {guest.customer.country}</div>
          </div>

          {/* Botón edit */}
          {permissions["reservations.update"] && (
            <button
              className="btn btn-sm fw-bold"
              style={{
                background: "linear-gradient(to right, #17a2b8, #006d91)",
                color: "white",
                border: "none",
                borderRadius: "5px",
                padding: "6px 12px",
                whiteSpace: "nowrap"
              }}
              onClick={() => {
                const mainCustomer = reservation.guests?.[0]?.customer;
                if (mainCustomer) {
                  setCustomerFormData({ ...mainCustomer });
                  setShowCustomerForm(true);
                }
              }}
            >
              Edit Customer
            </button>
          )}
        </div>
      </div>
    );
  })}




                    
        </div>
      )}
      
      <div style={{ display: "flex", minHeight: "100vh" }}>
        {/* ✅ Panel izquierdo fijo con info de la reserva */}
        <div
          className="bg-dark text-light shadow"
          style={{
            width: "45%",
            height: "100vh",
            padding: "20px",
            position: "fixed",
            top: "140px",
            left: 0,
            overflowY: "auto",
            zIndex: 1000,
            borderRight: "2px solid #333"
          }}
        >
          <h4 className="text-warning fw-bold mb-3">Reservation ID #{reservation.id}</h4>
          <p><strong>Check-in:</strong> {reservation.check_in_date}</p>
          <p><strong>Check-out:</strong> {reservation.check_out_date}</p>
          <p><strong>Room Nights:</strong> {reservation.room_nights}</p>
          <p><strong>Adults:</strong> {reservation.adults}</p>
          <p><strong>Children:</strong> {reservation.children}</p>
          <p><strong>Babies:</strong> {reservation.babies}</p>
          <p><strong>Room Category:</strong> {reservation.room_category?.name || "N/A"}</p>
          <p><strong>Rate:</strong> {reservation.rate?.name || "N/A"}</p>
          <p><strong>Total Price:</strong> {formatReservationAmount(reservation.total_price)}</p>
          <p><strong>Room:</strong> {reservation.room?.room_number || "Pending"}</p>
          <p><strong>Status:</strong> {reservation.status}</p>
          {permissions["reservations.view_payments"] && (
            <p><strong>Payment Status:</strong> {reservationPaymentStatus}</p>
          )}


          {/* 🔘 Botones con lógica de fecha y estado */}
          {(reservation.status === "Confirmed" || reservation.status === "Draft") && (
            <div className="mt-3 d-flex flex-column gap-2">
            {/* Primera fila: Check In + No Show */}
            {isCheckInTodayOrPast && (
              <div className="d-flex gap-2 flex-wrap">
                <button
                  className="btn btn-sm fw-bold px-3 py-1"
                  style={{
                    background: "linear-gradient(to right, #823dbe, #5B2C6F)",
                    color: "white",
                    border: "none",
                    borderRadius: "5px"
                  }}
                  onClick={handleCheckInClick}
                >
                  Check In
                </button>
                {reservation.status === "Confirmed" && (
                <button
                  className="btn btn-sm fw-bold px-3 py-1"
                  style={{
                    background: "linear-gradient(to right, #ffcc00, #cc3300)",
                    color: "white",
                    border: "none",
                    borderRadius: "5px"
                  }}
                  onClick={() => setShowNoShowDialog(true)}
                >
                  Mark as No Show
                </button>
                )}
              </div>
            )}
          
            {/* Segunda fila: Update + Cancel */}
            <div className="d-flex gap-2 flex-wrap">
              {permissions["reservations.update"] && (
              <button
                className="btn btn-sm fw-bold px-3 py-1"
                style={{
                  background: "linear-gradient(to right, #17a2b8, #006d91)",
                  color: "white",
                  border: "none",
                  borderRadius: "5px"
                }}
                onClick={() => {
                  const roomId = reservation.effective_room?.id || reservation.effective_room_id || reservation.room?.id || reservation.room_id || "";
                  navigate(`/edit-reservation/${reservation.id}?business_unit_id=${reservation.business_unit_id}&room_id=${roomId}`);
                }}
              >
                Update Reservation
              </button>
              )}
              {permissions["reservations.delete"] && (
              <button
                className="btn btn-sm fw-bold px-3 py-1"
                style={{
                  background: "linear-gradient(to right, #ffcc00, #cc3300)",
                  color: "white",
                  border: "none",
                  borderRadius: "5px"
                }}
                onClick={() => handleCancelReservation(reservation.id)}
              >
                Cancel Reservation
              </button>
              )}
            </div>
          </div>
          
          
          )}


          <hr className="border-secondary mt-4" />
          <div className="d-flex justify-content-between" style={{ fontSize: "0.85rem" }}>
            <span><strong>Created by:</strong> {reservation.created_by}</span>
            <span><strong>Last Update:</strong> {reservation.updatedAt}</span>
          </div>

        </div>
        {/* 🟣 Panel derecho con las tarjetas */}
        <div
          style={{
              display: "flex",
              flexDirection: "column",
              padding: "40px",
              paddingTop: "100px",
              backgroundColor: "#000",
              width: "100%",
              minHeight: "100vh" // ✅ Para que crezca si hay más contenido
          }}
        >


          {/* Formulario de edición del cliente (solo aparece si showCustomerForm es true) */}
          {showCustomerForm && (
            <div 
            className="card bg-dark text-light p-3 mb-2"
            style={{
              width: "fit-content",
              maxWidth: "50%",
              marginLeft: "auto",
              zoom: "75%",
              transformOrigin: "top right",
              fontSize: "0.9rem",
              lineHeight: "1.2",
              marginBottom: "0px"
              
            }}
            
          >
          
          
          
              <CustomerForm
                formData={customerFormData}
                handleChange={handleCustomerChange}
                handleSelectChange={handleCustomerSelectChange}
              />

              <div className="d-flex justify-content-between mt-3">
                <button 
                  type="button" 
                  onClick={handleSubmitCustomer}
                  className="btn btn-sm fw-bold px-3 py-1"
                  style={{
                    background: "linear-gradient(to right, #17a2b8, #006d91)",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    width: "48%"
                  }}
                >
                  Save Changes
                </button>

                <button 
                  type="button" 
                  onClick={() => setShowCustomerForm(false)}
                  className="btn btn-sm fw-bold px-3 py-1"
                  style={{
                    background: "linear-gradient(to right, #ffcc00, #cc3300)",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                    width: "48%"
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}


        
          {/* 🔸 Tarjeta de Payments */}
          <div
              className="card bg-dark text-light p-3"
              style={{
                  width: "50%",  // Controla el ancho que ocupa la tarjeta de Payments
                  fontSize: "0.9rem",
                  lineHeight: "1.2",
                  marginLeft: "auto", // Esto mueve la tarjeta de Payments a la derecha
                  
              }}
          >

            <h2 className="text-warning">Payments</h2>

              {/* 🔘 Botones + formulario de pago inline */}
              <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                {permissions["reservations.add_payment"] && (
                <button 
                  className="btn btn-sm fw-bold px-3 py-1"
                  style={{
                    backgroundColor: "#823dbe",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                  }}
                  onClick={handleAddPayment}
                >
                  {showPaymentForm ? "Cancel" : "Add New Payment"}
                </button>
                )}
                {permissions["reservations.add_payment"] && (
                <button 
                  className="btn btn-sm fw-bold px-3 py-1"
                  style={{
                    backgroundColor: "#e63946",
                    color: "white",
                    border: "none",
                    borderRadius: "5px",
                  }}
                  onClick={() => setShowRefundForm(!showRefundForm)}
                >
                  {showRefundForm ? "Cancel" : "Refund"}
                </button>
                )}
                {/* Formulario de pago */}
                {showPaymentForm && (
                  <form 
                    onSubmit={handleSubmitPayment} 
                    className="d-flex flex-wrap align-items-center gap-2"
                    style={{ backgroundColor: "#333", padding: "10px", borderRadius: "8px" }}
                  >
                    <input
                      type="number"
                      name="amount_paid"
                      placeholder={`Amount (${displayCurrency})`}
                      value={newPayment.amount_paid}
                      onChange={handlePaymentChange}
                      min={currencyAmountStep(displayCurrency, currencyCatalog)}
                      step={currencyAmountStep(displayCurrency, currencyCatalog)}
                      inputMode={getCurrencyMinorUnits(displayCurrency, currencyCatalog) === 0 ? "numeric" : "decimal"}
                      required
                      className="form-control form-control-sm"
                      style={{ width: "120px", backgroundColor: "#222", color: "white", border: "1px solid #555" }}
                    />

                    <select
                      name="payment_method"
                      value={newPayment.payment_method}
                      onChange={handlePaymentChange}
                      className="form-select form-select-sm"
                      style={{ width: "150px", backgroundColor: "#222", color: "white", border: "1px solid #555" }}
                    >
                      <option value="Cash">Cash</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Other">Other</option>
                    </select>

                    <button 
                      type="submit" 
                      className="btn btn-success btn-sm fw-bold px-3"
                    >
                      Confirm
                    </button>
                  </form>
                )}

                {/* Formulario de reembolso */}
                {showRefundForm && (
                  <form 
                    onSubmit={handleSubmitRefund} 
                    className="d-flex flex-wrap align-items-center gap-2"
                    style={{ backgroundColor: "#333", padding: "10px", borderRadius: "8px" }}
                  >
                    <input 
                      type="number" 
                      name="refund_amount" 
                      value={refundData.refund_amount} 
                      onChange={handleRefundChange}
                      placeholder={`Refund (${displayCurrency})`}
                      min={currencyAmountStep(displayCurrency, currencyCatalog)}
                      step={currencyAmountStep(displayCurrency, currencyCatalog)}
                      inputMode={getCurrencyMinorUnits(displayCurrency, currencyCatalog) === 0 ? "numeric" : "decimal"}
                      required
                      className="form-control form-control-sm"
                      style={{ width: "100px", backgroundColor: "#222", color: "white", border: "1px solid #555" }}
                    />

                    <select
                      name="refund_method"
                      value={refundData.refund_method}
                      onChange={handleRefundChange}
                      required
                      className="form-select form-select-sm"
                      style={{ width: "150px", backgroundColor: "#222", color: "white", border: "1px solid #555" }}
                    >
                      <option value="">Method</option>
                      <option value="Cash">Cash</option>
                      <option value="Credit Card">Credit Card</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Other">Other</option>
                    </select>

                    <button 
                      type="submit" 
                      className="btn btn-danger btn-sm fw-bold px-3"
                    >
                      Confirm
                    </button>
                  </form>
                )}
              </div>

              {/* Balance nuevo por reserva */}
              {permissions["reservations.view_payments"] ? (
                <div className="payments-list mt-3">
                  <div className="payment-summary mt-3">
                    <div className="d-flex py-2 border-bottom border-dark-subtle">
                      <div className="col fw-bold">Reservation total</div>
                      <div className="col text-end fw-bold">{formatReservationAmount(reservationTotalCents / 100)}</div>
                    </div>
                    <div className="d-flex py-2 border-bottom border-dark-subtle">
                      <div className="col fw-bold">Assigned paid</div>
                      <div className="col text-end fw-bold">{formatReservationAmount(reservationPaidCents / 100)}</div>
                    </div>
                    <div className="d-flex py-2 border-bottom border-dark-subtle">
                      <div className="col fw-bold">Pending</div>
                      <div className="col text-end fw-bold">{formatReservationAmount(reservationPendingCents / 100)}</div>
                    </div>
                    <div className="d-flex py-2">
                      <div className="col fw-bold">Status</div>
                      <div className="col text-end fw-bold">{reservationPaymentStatus}</div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

        </div>
      </div>
        <PmsConfirmDialog
          isOpen={showNoShowDialog}
          eyebrow="Front Desk"
          title="Mark reservation as No Show?"
          message={`This will update reservation #${reservation?.id || id} to No Show and remove it from active check-in workflows. No no-show charge will be applied.`}
          confirmLabel={isNoShowSaving ? "Saving..." : "No Show"}
          cancelLabel="Back"
          onClose={() => setShowNoShowDialog(false)}
          onConfirm={() => {
            void handleMarkNoShow(reservation.id);
          }}
          isBusy={isNoShowSaving}
          tone="warning"
        />
      </div>

    );
  }
  
export default ReservationDetails;
